import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SearchBar from "@/components/feature/SearchBar";
import FilmCard from "@/components/base/FilmCard";
import ActorCard from "@/components/base/ActorCard";
import EmptyState from "@/components/base/EmptyState";
import { searchAll, trendingFilms, trendingActors } from "@/utils/catalog";
import { useSeo } from "@/hooks/useSeo";

type Tab = "all" | "film" | "actor";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get("q") ?? "").trim();
  const [tab, setTab] = useState<Tab>("all");

  useSeo(
    q ? `“${q}”的搜索结果 - 葵司影视资讯` : "搜索 - 葵司影视资讯",
    q
      ? `葵司为你找到与“${q}”相关的影视作品与演员资料，支持在影视作品与演员之间切换查看结果。`
      : "在葵司搜索影视作品、演员姓名或题材关键词，快速找到对应的资料与动态。",
    "葵司,影视搜索,演员搜索,影视作品查询",
  );

  const { films, actors } = useMemo(() => {
    const result = searchAll(q);
    return {
      films: [...result.films].sort((a, b) => b.popularity - a.popularity),
      actors: [...result.actors].sort((a, b) => b.popularity - a.popularity),
    };
  }, [q]);

  const total = films.length + actors.length;

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "all", label: "全部", count: total },
    { key: "film", label: "影视作品", count: films.length },
    { key: "actor", label: "演员", count: actors.length },
  ];

  const showFilms = tab === "all" || tab === "film";
  const showActors = tab === "all" || tab === "actor";

  const handleTab = (key: Tab) => setTab(key);

  const recommendations = (
    <div className="mt-10">
      <h2 className="font-heading font-bold text-lg text-foreground-950">
        为你推荐
      </h2>
      <p className="mt-2 text-sm text-foreground-500">
        没有找到想要的内容？看看这些热门作品与演员。
      </p>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {trendingFilms(4).map((f) => (
          <FilmCard key={f.id} film={f} />
        ))}
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {trendingActors(4).map((a) => (
          <ActorCard key={a.id} actor={a} />
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
            站内搜索
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            支持影视作品名称、演员姓名与题材关键词的模糊匹配。
          </p>
          <div className="mt-5 max-w-2xl">
            <SearchBar key={q} initialValue={q} size="lg" />
          </div>
          {q && (
            <p className="mt-4 text-sm text-foreground-600">
              当前搜索关键词：
              <span className="ml-1 text-accent-600 font-medium">“{q}”</span>
              <span className="ml-2 text-foreground-400">
                共 {total} 条结果
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
        {!q ? (
          <>
            <EmptyState
              icon="ri-search-line"
              title="开始你的影视探索"
              description="输入影视作品名称、演员姓名或题材关键词，例如“悬疑”“科幻”“林知遥”。"
            />
            {recommendations}
          </>
        ) : total === 0 ? (
          <>
            <div className="rounded-lg border border-background-200 bg-background-50">
              <EmptyState
                icon="ri-emotion-sad-line"
                title={`没有找到与“${q}”相关的内容`}
                description="试试更换关键词，或浏览下方为你推荐的热门内容。"
                actionLabel="清除关键词"
                onAction={() => setSearchParams({})}
              />
            </div>
            {recommendations}
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => handleTab(t.key)}
                  className={`px-4 h-9 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    tab === t.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-accent-600"
                  }`}
                >
                  {t.label}
                  <span className="ml-1 text-xs opacity-80">{t.count}</span>
                </button>
              ))}
            </div>

            {showFilms && films.length > 0 && (
              <section className="mt-8">
                <h2 className="font-heading font-bold text-lg text-foreground-950">
                  影视作品
                </h2>
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {films.map((f) => (
                    <FilmCard key={f.id} film={f} />
                  ))}
                </div>
              </section>
            )}

            {showActors && actors.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading font-bold text-lg text-foreground-950">
                  演员
                </h2>
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {actors.map((a) => (
                    <ActorCard key={a.id} actor={a} />
                  ))}
                </div>
              </section>
            )}

            <div className="mt-10">
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-accent-600"
              >
                <i className="ri-arrow-left-line" />
                返回首页
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}